/**
 * ZBufferGPUEngine.ts
 * WebGPU-accelerated Z-Buffer engine for CNC stock simulation
 *
 * Uses compute shaders to maintain 6-directional depth textures (stored as GPU storage buffers).
 * Each view tracks near/far depth for efficient material removal simulation.
 *
 * Architecture:
 * - 6 depth view pairs (nearDepth + farDepth) as GPU storage buffers
 * - Compute shader rasterizes tool footprint into affected views
 * - Sub-region dispatch: only process pixels within tool projection
 * - Primary focus: +Z/-Z views (main CNC milling direction)
 */

import { VoxelToolGeometry } from './VoxelCSGEngine';

// WGSL compute shader for depth buffer update
const ZBUFFER_UPDATE_SHADER = `
struct ViewInfo {
  direction: u32,        // 0=+X, 1=-X, 2=+Y, 3=-Y, 4=+Z, 5=-Z
  resolutionU: u32,      // Resolution in U direction
  resolutionV: u32,      // Resolution in V direction
  originX: f32,          // World origin for this view
  originY: f32,
  originZ: f32,
  pixelSize: f32,        // Size of one pixel in world units
  padding: u32,
}

struct ToolInfo {
  posX: f32,
  posY: f32,
  posZ: f32,
  toolType: u32,         // 0=cylindrical, 1=spherical, 2=conical
  radius: f32,
  height: f32,           // Tool height/length
  angle: f32,            // Cone angle for conical tools
  dirX: f32,             // Tool direction vector
  dirY: f32,
  dirZ: f32,
  padding1: u32,
  padding2: u32,
}

struct DispatchInfo {
  offsetU: u32,          // Start pixel offset in U
  offsetV: u32,          // Start pixel offset in V
  countU: u32,           // Number of pixels to process in U
  countV: u32,           // Number of pixels to process in V
}

@group(0) @binding(0) var<uniform> viewInfo: ViewInfo;
@group(0) @binding(1) var<uniform> toolInfo: ToolInfo;
@group(0) @binding(2) var<uniform> dispatchInfo: DispatchInfo;
@group(0) @binding(3) var<storage, read_write> nearDepth: array<f32>;
@group(0) @binding(4) var<storage, read_write> farDepth: array<f32>;

// For batch mode: positions buffer (6 floats per segment: ax,ay,az,bx,by,bz)
@group(0) @binding(5) var<storage, read> positions: array<f32>;

fn pixelToWorld(u: u32, v: u32, direction: u32) -> vec3f {
  let fu = f32(u);
  let fv = f32(v);
  let ps = viewInfo.pixelSize;
  let ox = viewInfo.originX;
  let oy = viewInfo.originY;
  let oz = viewInfo.originZ;

  switch direction {
    case 0u: { // +X view (looking along +X)
      return vec3f(ox, oy + fu * ps, oz + fv * ps);
    }
    case 1u: { // -X view (looking along -X)
      return vec3f(ox, oy + fu * ps, oz + fv * ps);
    }
    case 2u: { // +Y view (looking along +Y)
      return vec3f(ox + fu * ps, oy, oz + fv * ps);
    }
    case 3u: { // -Y view (looking along -Y)
      return vec3f(ox + fu * ps, oy, oz + fv * ps);
    }
    case 4u: { // +Z view (looking down along +Z)
      return vec3f(ox + fu * ps, oy + fv * ps, oz);
    }
    default: { // 5u: -Z view (looking up along -Z)
      return vec3f(ox + fu * ps, oy + fv * ps, oz);
    }
  }
}

fn isInsideToolProjection(worldPos: vec3f, tool: ToolInfo) -> bool {
  let dx = worldPos.x - tool.posX;
  let dy = worldPos.y - tool.posY;
  let dz = worldPos.z - tool.posZ;

  switch tool.toolType {
    case 0u: { // Cylindrical
      let dist2 = dx * dx + dy * dy;
      return dist2 <= tool.radius * tool.radius;
    }
    case 1u: { // Spherical
      let dist2 = dx * dx + dy * dy + dz * dz;
      return dist2 <= tool.radius * tool.radius;
    }
    case 2u: { // Conical
      // Simplified cone test: distance from axis vs height-based radius
      let axisLen = sqrt(tool.dirX * tool.dirX + tool.dirY * tool.dirY + tool.dirZ * tool.dirZ);
      if axisLen < 0.001 {
        return false;
      }
      let axisX = tool.dirX / axisLen;
      let axisY = tool.dirY / axisLen;
      let axisZ = tool.dirZ / axisLen;

      let t = dx * axisX + dy * axisY + dz * axisZ;
      if t < 0.0 || t > tool.height {
        return false;
      }

      let projX = tool.posX + axisX * t;
      let projY = tool.posY + axisY * t;
      let projZ = tool.posZ + axisZ * t;

      let radialDist = sqrt(
        (worldPos.x - projX) * (worldPos.x - projX) +
        (worldPos.y - projY) * (worldPos.y - projY) +
        (worldPos.z - projZ) * (worldPos.z - projZ)
      );

      let radiusAtT = tool.radius + (tool.height - t) * tan(tool.angle);
      return radialDist <= radiusAtT;
    }
    default: {
      return false;
    }
  }
}

fn computeToolDepth(worldPos: vec3f, tool: ToolInfo, direction: u32) -> f32 {
  // Return the depth coordinate (near surface) where tool removes material
  let dx = worldPos.x - tool.posX;
  let dy = worldPos.y - tool.posY;
  let dz = worldPos.z - tool.posZ;

  switch direction {
    case 0u: { // +X
      return tool.posX + tool.radius;
    }
    case 1u: { // -X
      return tool.posX - tool.radius;
    }
    case 2u: { // +Y
      return tool.posY + tool.radius;
    }
    case 3u: { // -Y
      return tool.posY - tool.radius;
    }
    case 4u: { // +Z (looking down)
      // For cylindrical tool pointing down, near surface is tool bottom
      if tool.toolType == 0u {
        return tool.posZ - tool.height;
      } else if tool.toolType == 1u {
        // Spherical: compute intersection
        let dist2d = sqrt(dx * dx + dy * dy);
        if dist2d <= tool.radius {
          let depthOffset = sqrt(tool.radius * tool.radius - dist2d * dist2d);
          return tool.posZ - depthOffset;
        }
      }
      return tool.posZ;
    }
    default: { // 5u: -Z
      if tool.toolType == 0u {
        return tool.posZ + tool.height;
      } else if tool.toolType == 1u {
        let dist2d = sqrt(dx * dx + dy * dy);
        if dist2d <= tool.radius {
          let depthOffset = sqrt(tool.radius * tool.radius - dist2d * dist2d);
          return tool.posZ + depthOffset;
        }
      }
      return tool.posZ;
    }
  }
}

@compute @workgroup_size(8, 8, 1)
fn main(@builtin(global_invocation_id) global_id: vec3u) {
  let u = dispatchInfo.offsetU + global_id.x;
  let v = dispatchInfo.offsetV + global_id.y;

  if u >= viewInfo.resolutionU || v >= viewInfo.resolutionV {
    return;
  }

  if global_id.x >= dispatchInfo.countU || global_id.y >= dispatchInfo.countV {
    return;
  }

  let pixelIdx = v * viewInfo.resolutionU + u;
  let worldPos = pixelToWorld(u, v, viewInfo.direction);

  // Check if this pixel is inside tool projection
  if isInsideToolProjection(worldPos, toolInfo) {
    let toolDepth = computeToolDepth(worldPos, toolInfo, viewInfo.direction);

    // Update depth buffer
    // For +Z view: material removed means nearDepth increases (moves down)
    // For -Z view: material removed means nearDepth decreases (moves up)
    let currentNear = nearDepth[pixelIdx];
    let currentFar = farDepth[pixelIdx];

    switch viewInfo.direction {
      case 0u, 2u, 4u: { // Positive directions
        nearDepth[pixelIdx] = max(currentNear, toolDepth);
      }
      default: { // Negative directions
        nearDepth[pixelIdx] = min(currentNear, toolDepth);
      }
    }

    // Check if fully cut through
    if viewInfo.direction == 4u || viewInfo.direction == 5u {
      if abs(nearDepth[pixelIdx] - currentFar) < 0.001 {
        // Pixel fully cut through
        nearDepth[pixelIdx] = currentFar;
      }
    }
  }
}
`;

interface ViewConfig {
  direction: number; // 0=+X, 1=-X, 2=+Y, 3=-Y, 4=+Z, 5=-Z
  resolutionU: number;
  resolutionV: number;
  originX: number;
  originY: number;
  originZ: number;
  pixelSize: number;
}

interface BBoxInfo {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  minZ: number;
  maxZ: number;
}

export class ZBufferGPUEngine {
  private device: GPUDevice | null = null;
  private pipeline: GPUComputePipeline | null = null;
  private bindGroupLayout: GPUBindGroupLayout | null = null;

  private viewConfigs: ViewConfig[] = [];
  private viewBuffers: Array<{
    nearDepth: GPUBuffer;
    farDepth: GPUBuffer;
    nearStaging: GPUBuffer;
    farStaging: GPUBuffer;
  }> = [];

  private uniformBuffer: GPUBuffer | null = null;
  private toolUniformBuffer: GPUBuffer | null = null;
  private dispatchUniformBuffer: GPUBuffer | null = null;
  private positionsBuffer: GPUBuffer | null = null; // For batch mode

  private bbox: BBoxInfo | null = null;
  private voxelSize = 0;
  private resXY = 0; // For Z views
  private resXZ = 0; // For Y views
  private resYZ = 0; // For X views

  private initialized = false;
  private pendingOps: GPUCommandEncoder[] = [];

  // Pre-allocated uniform data (avoid GC in hot path)
  private viewUniformData = new Uint32Array(8);
  private viewUniformFloatView = new Float32Array(this.viewUniformData.buffer);
  private toolUniformData = new Float32Array(12);
  private toolUniformIntView = new Uint32Array(this.toolUniformData.buffer);
  private dispatchUniformData = new Uint32Array(4);

  // Cached bind groups per view (buffers don't change, only uniforms)
  private viewBindGroups: GPUBindGroup[] = [];

  // Cached solid init data per view (avoid allocation in initializeSolid)
  private solidInitDataPerView: Array<{near: Float32Array, far: Float32Array}> = [];

  constructor() {}

  async initialize(
    bbox: BBoxInfo,
    voxelSize: number
  ): Promise<boolean> {
    try {
      // Request WebGPU adapter and device
      const adapter = await (navigator as any).gpu?.requestAdapter({ powerPreference: 'high-performance' });
      if (!adapter) {
        console.warn('ZBufferGPUEngine: WebGPU not supported');
        return false;
      }

      // Request device with higher buffer limits from adapter
      const adapterMaxBuffer = (adapter.limits as any).maxBufferSize || 268435456;
      this.device = await adapter.requestDevice({
        requiredLimits: {
          maxBufferSize: adapterMaxBuffer,
          maxStorageBufferBindingSize: adapter.limits.maxStorageBufferBindingSize,
        },
      });
      if (!this.device) {
        console.warn('ZBufferGPUEngine: Failed to get GPU device');
        return false;
      }

      this.bbox = bbox;
      this.voxelSize = voxelSize;

      // Compute resolutions
      const sizeX = bbox.maxX - bbox.minX;
      const sizeY = bbox.maxY - bbox.minY;
      const sizeZ = bbox.maxZ - bbox.minZ;

      this.resXY = Math.ceil(Math.max(sizeX, sizeY) / voxelSize);
      this.resXZ = Math.ceil(Math.max(sizeX, sizeZ) / voxelSize);
      this.resYZ = Math.ceil(Math.max(sizeY, sizeZ) / voxelSize);

      // Setup view configurations
      this.setupViewConfigs();

      // Create shader module
      const shaderModule = this.device.createShaderModule({
        code: ZBUFFER_UPDATE_SHADER,
      } as any);

      // Create bind group layout
      this.bindGroupLayout = this.device.createBindGroupLayout({
        entries: [
          { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } }, // viewInfo
          { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } }, // toolInfo
          { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'uniform' } }, // dispatchInfo
          { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // nearDepth
          { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'storage' } }, // farDepth
          { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: 'read-only-storage' } }, // positions
        ],
      } as any);

      // Create pipeline
      this.pipeline = this.device.createComputePipeline({
        layout: this.device.createPipelineLayout({
          bindGroupLayouts: [this.bindGroupLayout]
        }),
        compute: {
          module: shaderModule,
          entryPoint: 'main'
        },
      } as any);

      // Allocate uniform buffers
      this.uniformBuffer = this.device.createBuffer({
        size: 32, // ViewInfo struct (8 fields * 4 bytes)
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });

      this.toolUniformBuffer = this.device.createBuffer({
        size: 48, // ToolInfo struct (12 fields * 4 bytes)
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });

      this.dispatchUniformBuffer = this.device.createBuffer({
        size: 16, // DispatchInfo struct (4 fields * 4 bytes)
        usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
      });

      // Allocate positions buffer (max 10000 segments = 240KB)
      this.positionsBuffer = this.device.createBuffer({
        size: 10000 * 6 * 4,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
      });

      // Create depth buffers for each view
      this.createDepthBuffers();

      // Create cached bind groups for each view
      this.createViewBindGroups();

      // Pre-allocate solid init data
      this.preallocateSolidInitData();

      this.initialized = true;

      return true;

    } catch (error) {
      console.error('ZBufferGPUEngine initialization failed:', error);
      this.dispose();
      return false;
    }
  }

  private setupViewConfigs(): void {
    if (!this.bbox) return;

    const { minX, maxX, minY, maxY, minZ, maxZ } = this.bbox;

    // +X view (YZ plane)
    this.viewConfigs.push({
      direction: 0,
      resolutionU: this.resYZ,
      resolutionV: this.resYZ,
      originX: minX,
      originY: minY,
      originZ: minZ,
      pixelSize: this.voxelSize
    });

    // -X view (YZ plane)
    this.viewConfigs.push({
      direction: 1,
      resolutionU: this.resYZ,
      resolutionV: this.resYZ,
      originX: maxX,
      originY: minY,
      originZ: minZ,
      pixelSize: this.voxelSize
    });

    // +Y view (XZ plane)
    this.viewConfigs.push({
      direction: 2,
      resolutionU: this.resXZ,
      resolutionV: this.resXZ,
      originX: minX,
      originY: minY,
      originZ: minZ,
      pixelSize: this.voxelSize
    });

    // -Y view (XZ plane)
    this.viewConfigs.push({
      direction: 3,
      resolutionU: this.resXZ,
      resolutionV: this.resXZ,
      originX: minX,
      originY: maxY,
      originZ: minZ,
      pixelSize: this.voxelSize
    });

    // +Z view (XY plane) - PRIMARY for CNC
    this.viewConfigs.push({
      direction: 4,
      resolutionU: this.resXY,
      resolutionV: this.resXY,
      originX: minX,
      originY: minY,
      originZ: maxZ,
      pixelSize: this.voxelSize
    });

    // -Z view (XY plane)
    this.viewConfigs.push({
      direction: 5,
      resolutionU: this.resXY,
      resolutionV: this.resXY,
      originX: minX,
      originY: minY,
      originZ: minZ,
      pixelSize: this.voxelSize
    });
  }

  private createDepthBuffers(): void {
    if (!this.device) return;

    for (const config of this.viewConfigs) {
      const bufferSize = config.resolutionU * config.resolutionV * 4; // Float32

      const nearDepth = this.device.createBuffer({
        size: bufferSize,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
      });

      const farDepth = this.device.createBuffer({
        size: bufferSize,
        usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC | GPUBufferUsage.COPY_DST,
      });

      const nearStaging = this.device.createBuffer({
        size: bufferSize,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      const farStaging = this.device.createBuffer({
        size: bufferSize,
        usage: GPUBufferUsage.MAP_READ | GPUBufferUsage.COPY_DST,
      });

      this.viewBuffers.push({ nearDepth, farDepth, nearStaging, farStaging });
    }
  }

  private createViewBindGroups(): void {
    if (!this.device || !this.bindGroupLayout || !this.uniformBuffer ||
        !this.toolUniformBuffer || !this.dispatchUniformBuffer || !this.positionsBuffer) return;

    // Create bind groups for each view (reuse them since buffers don't change)
    for (let i = 0; i < this.viewBuffers.length; i++) {
      const buffers = this.viewBuffers[i];
      const bindGroup = this.device.createBindGroup({
        layout: this.bindGroupLayout,
        entries: [
          { binding: 0, resource: { buffer: this.uniformBuffer } },
          { binding: 1, resource: { buffer: this.toolUniformBuffer } },
          { binding: 2, resource: { buffer: this.dispatchUniformBuffer } },
          { binding: 3, resource: { buffer: buffers.nearDepth } },
          { binding: 4, resource: { buffer: buffers.farDepth } },
          { binding: 5, resource: { buffer: this.positionsBuffer } },
        ]
      });
      this.viewBindGroups.push(bindGroup);
    }
  }

  private preallocateSolidInitData(): void {
    if (!this.bbox) return;

    for (let i = 0; i < this.viewConfigs.length; i++) {
      const config = this.viewConfigs[i];
      const count = config.resolutionU * config.resolutionV;

      const nearData = new Float32Array(count);
      const farData = new Float32Array(count);

      // Initialize based on direction
      switch (config.direction) {
        case 0: // +X
          nearData.fill(this.bbox.minX);
          farData.fill(this.bbox.maxX);
          break;
        case 1: // -X
          nearData.fill(this.bbox.maxX);
          farData.fill(this.bbox.minX);
          break;
        case 2: // +Y
          nearData.fill(this.bbox.minY);
          farData.fill(this.bbox.maxY);
          break;
        case 3: // -Y
          nearData.fill(this.bbox.maxY);
          farData.fill(this.bbox.minY);
          break;
        case 4: // +Z
          nearData.fill(this.bbox.maxZ);
          farData.fill(this.bbox.minZ);
          break;
        case 5: // -Z
          nearData.fill(this.bbox.minZ);
          farData.fill(this.bbox.maxZ);
          break;
      }

      this.solidInitDataPerView.push({ near: nearData, far: farData });
    }
  }

  initializeSolid(): void {
    if (!this.device || !this.bbox) return;

    // Use pre-allocated solid init data (zero allocation)
    for (let i = 0; i < this.viewConfigs.length; i++) {
      const buffers = this.viewBuffers[i];
      const initData = this.solidInitDataPerView[i];

      this.device.queue.writeBuffer(buffers.nearDepth, 0, initData.near);
      this.device.queue.writeBuffer(buffers.farDepth, 0, initData.far);
    }
  }

  removeToolGPU(
    x: number, y: number, z: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): void {
    if (!this.initialized || !this.device || !this.pipeline) return;

    // Focus on +Z and -Z views (primary CNC directions)
    const viewIndices = [4, 5]; // Can expand to [0,1,2,3,4,5] for all views

    for (const viewIdx of viewIndices) {
      this.removeToolFromView(viewIdx, x, y, z, tool, dirX, dirY, dirZ);
    }
  }

  private removeToolFromView(
    viewIdx: number,
    x: number, y: number, z: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): void {
    if (!this.device || !this.pipeline) return;

    const config = this.viewConfigs[viewIdx];

    // Compute tool footprint bounds in pixel space
    const toolRadius = tool.diameter / 2;

    const { offsetU, offsetV, countU, countV } = this.computeFootprint(
      config, x, y, z, toolRadius
    );

    if (countU === 0 || countV === 0) return;

    // Upload uniforms (uses pre-allocated buffers)
    this.uploadViewUniform(config);
    this.uploadToolUniform(x, y, z, tool, dirX, dirY, dirZ);
    this.uploadDispatchUniform(offsetU, offsetV, countU, countV);

    // Use cached bind group
    const bindGroup = this.viewBindGroups[viewIdx];

    // Dispatch compute
    const encoder = this.device.createCommandEncoder();
    const pass = encoder.beginComputePass();
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, bindGroup);

    const workgroupsX = Math.ceil(countU / 8);
    const workgroupsY = Math.ceil(countV / 8);
    pass.dispatchWorkgroups(workgroupsX, workgroupsY, 1);
    pass.end();

    this.device.queue.submit([encoder.finish()]);
  }

  private computeFootprint(
    config: ViewConfig,
    x: number, y: number, z: number,
    radius: number
  ): { offsetU: number; offsetV: number; countU: number; countV: number } {
    // Map tool position to pixel coordinates for this view
    let u = 0, v = 0;

    switch (config.direction) {
      case 0: case 1: // X views (YZ plane)
        u = Math.floor((y - config.originY) / config.pixelSize);
        v = Math.floor((z - config.originZ) / config.pixelSize);
        break;
      case 2: case 3: // Y views (XZ plane)
        u = Math.floor((x - config.originX) / config.pixelSize);
        v = Math.floor((z - config.originZ) / config.pixelSize);
        break;
      case 4: case 5: // Z views (XY plane)
        u = Math.floor((x - config.originX) / config.pixelSize);
        v = Math.floor((y - config.originY) / config.pixelSize);
        break;
    }

    const radiusPixels = Math.ceil(radius / config.pixelSize);

    const offsetU = Math.max(0, u - radiusPixels);
    const offsetV = Math.max(0, v - radiusPixels);
    const endU = Math.min(config.resolutionU, u + radiusPixels + 1);
    const endV = Math.min(config.resolutionV, v + radiusPixels + 1);

    return {
      offsetU,
      offsetV,
      countU: endU - offsetU,
      countV: endV - offsetV
    };
  }

  private uploadViewUniform(config: ViewConfig): void {
    if (!this.device || !this.uniformBuffer) return;

    // Use pre-allocated buffer (zero allocation)
    this.viewUniformData[0] = config.direction;
    this.viewUniformData[1] = config.resolutionU;
    this.viewUniformData[2] = config.resolutionV;

    this.viewUniformFloatView[3] = config.originX;
    this.viewUniformFloatView[4] = config.originY;
    this.viewUniformFloatView[5] = config.originZ;
    this.viewUniformFloatView[6] = config.pixelSize;

    this.device.queue.writeBuffer(this.uniformBuffer, 0, this.viewUniformData);
  }

  private uploadToolUniform(
    x: number, y: number, z: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): void {
    if (!this.device || !this.toolUniformBuffer) return;

    // Use pre-allocated buffer (zero allocation)
    this.toolUniformData[0] = x;
    this.toolUniformData[1] = y;
    this.toolUniformData[2] = z;

    const typeMap: Record<string, number> = { 'flat': 0, 'ball': 1, 'bull': 2 };
    this.toolUniformIntView[3] = typeMap[tool.type] ?? 0;

    this.toolUniformData[4] = tool.diameter / 2;  // radius
    this.toolUniformData[5] = tool.length;        // height
    this.toolUniformData[6] = tool.cornerRadius;  // corner radius (used for bull nose)
    this.toolUniformData[7] = dirX;
    this.toolUniformData[8] = dirY;
    this.toolUniformData[9] = dirZ;

    this.device.queue.writeBuffer(this.toolUniformBuffer, 0, this.toolUniformData);
  }

  private uploadDispatchUniform(offsetU: number, offsetV: number, countU: number, countV: number): void {
    if (!this.device || !this.dispatchUniformBuffer) return;

    // Use pre-allocated buffer (zero allocation)
    this.dispatchUniformData[0] = offsetU;
    this.dispatchUniformData[1] = offsetV;
    this.dispatchUniformData[2] = countU;
    this.dispatchUniformData[3] = countV;

    this.device.queue.writeBuffer(this.dispatchUniformBuffer, 0, this.dispatchUniformData);
  }

  removeSweptGPU(
    ax: number, ay: number, az: number,
    bx: number, by: number, bz: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): void {
    if (!this.initialized || !this.device || !this.pipeline) return;

    // Simple swept removal: sample along line segment
    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);

    if (len < this.voxelSize * 0.5) {
      // Too short, just remove at point A
      this.removeToolGPU(ax, ay, az, tool, dirX, dirY, dirZ);
      return;
    }

    const steps = Math.ceil(len / (this.voxelSize * 0.5));

    // Pre-compute all sample positions
    const samplePositions: Array<{x: number, y: number, z: number}> = [];
    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      samplePositions.push({
        x: ax + dx * t,
        y: ay + dy * t,
        z: az + dz * t
      });
    }

    // Focus on +Z and -Z views (primary CNC directions)
    const viewIndices = [4, 5];
    const toolRadius = tool.diameter / 2;

    // Batch all command buffers and submit once
    const commandBuffers: GPUCommandBuffer[] = [];

    for (const viewIdx of viewIndices) {
      const config = this.viewConfigs[viewIdx];
      const bindGroup = this.viewBindGroups[viewIdx];

      // Upload view and tool uniforms once per view (they don't change per sample)
      this.uploadViewUniform(config);
      this.uploadToolUniform(ax, ay, az, tool, dirX, dirY, dirZ);

      // For each sample position
      for (const pos of samplePositions) {
        const { offsetU, offsetV, countU, countV } = this.computeFootprint(
          config, pos.x, pos.y, pos.z, toolRadius
        );

        if (countU === 0 || countV === 0) continue;

        // Update tool position for this sample
        this.uploadToolUniform(pos.x, pos.y, pos.z, tool, dirX, dirY, dirZ);
        this.uploadDispatchUniform(offsetU, offsetV, countU, countV);

        // Create command encoder for this sample
        const encoder = this.device.createCommandEncoder();
        const pass = encoder.beginComputePass();
        pass.setPipeline(this.pipeline);
        pass.setBindGroup(0, bindGroup);

        const workgroupsX = Math.ceil(countU / 8);
        const workgroupsY = Math.ceil(countV / 8);
        pass.dispatchWorkgroups(workgroupsX, workgroupsY, 1);
        pass.end();

        commandBuffers.push(encoder.finish());
      }
    }

    // ONE submit with all command buffers (instead of N submits)
    if (commandBuffers.length > 0) {
      this.device.queue.submit(commandBuffers);
    }
  }

  removeBatchGPU(
    positions: Float32Array,
    numPositions: number,
    tool: VoxelToolGeometry,
    dirX: number, dirY: number, dirZ: number
  ): void {
    if (!this.initialized || !this.device || !this.pipeline) return;

    // Batch mode: process all segments with batched submit
    const viewIndices = [4, 5];
    const toolRadius = tool.diameter / 2;

    // Collect all command buffers
    const commandBuffers: GPUCommandBuffer[] = [];

    for (const viewIdx of viewIndices) {
      const config = this.viewConfigs[viewIdx];
      const bindGroup = this.viewBindGroups[viewIdx];

      // Upload view uniform once per view
      this.uploadViewUniform(config);

      // Process each segment
      for (let i = 0; i < numPositions - 1; i++) {
        const ax = positions[i * 3];
        const ay = positions[i * 3 + 1];
        const az = positions[i * 3 + 2];
        const bx = positions[(i + 1) * 3];
        const by = positions[(i + 1) * 3 + 1];
        const bz = positions[(i + 1) * 3 + 2];

        // Compute samples for this segment
        const dx = bx - ax;
        const dy = by - ay;
        const dz = bz - az;
        const len = Math.sqrt(dx * dx + dy * dy + dz * dz);

        if (len < this.voxelSize * 0.5) {
          // Single sample
          const { offsetU, offsetV, countU, countV } = this.computeFootprint(
            config, ax, ay, az, toolRadius
          );

          if (countU === 0 || countV === 0) continue;

          this.uploadToolUniform(ax, ay, az, tool, dirX, dirY, dirZ);
          this.uploadDispatchUniform(offsetU, offsetV, countU, countV);

          const encoder = this.device.createCommandEncoder();
          const pass = encoder.beginComputePass();
          pass.setPipeline(this.pipeline);
          pass.setBindGroup(0, bindGroup);

          const workgroupsX = Math.ceil(countU / 8);
          const workgroupsY = Math.ceil(countV / 8);
          pass.dispatchWorkgroups(workgroupsX, workgroupsY, 1);
          pass.end();

          commandBuffers.push(encoder.finish());
        } else {
          // Multiple samples along segment
          const steps = Math.ceil(len / (this.voxelSize * 0.5));
          for (let j = 0; j <= steps; j++) {
            const t = j / steps;
            const x = ax + dx * t;
            const y = ay + dy * t;
            const z = az + dz * t;

            const { offsetU, offsetV, countU, countV } = this.computeFootprint(
              config, x, y, z, toolRadius
            );

            if (countU === 0 || countV === 0) continue;

            this.uploadToolUniform(x, y, z, tool, dirX, dirY, dirZ);
            this.uploadDispatchUniform(offsetU, offsetV, countU, countV);

            const encoder = this.device.createCommandEncoder();
            const pass = encoder.beginComputePass();
            pass.setPipeline(this.pipeline);
            pass.setBindGroup(0, bindGroup);

            const workgroupsX = Math.ceil(countU / 8);
            const workgroupsY = Math.ceil(countV / 8);
            pass.dispatchWorkgroups(workgroupsX, workgroupsY, 1);
            pass.end();

            commandBuffers.push(encoder.finish());
          }
        }
      }
    }

    // ONE submit with all command buffers
    if (commandBuffers.length > 0) {
      this.device.queue.submit(commandBuffers);
    }
  }

  async readDepthData(viewIndex: number): Promise<{ near: Float32Array; far: Float32Array } | null> {
    if (!this.device || viewIndex < 0 || viewIndex >= this.viewBuffers.length) return null;

    const config = this.viewConfigs[viewIndex];
    const buffers = this.viewBuffers[viewIndex];
    const bufferSize = config.resolutionU * config.resolutionV * 4;

    // Copy to staging buffers (one encoder for both copies)
    const encoder = this.device.createCommandEncoder();
    encoder.copyBufferToBuffer(buffers.nearDepth, 0, buffers.nearStaging, 0, bufferSize);
    encoder.copyBufferToBuffer(buffers.farDepth, 0, buffers.farStaging, 0, bufferSize);
    this.device.queue.submit([encoder.finish()]);

    // Map both buffers in parallel
    await Promise.all([
      buffers.nearStaging.mapAsync(GPUMapMode.READ),
      buffers.farStaging.mapAsync(GPUMapMode.READ)
    ]);

    const nearData = new Float32Array(buffers.nearStaging.getMappedRange().slice(0));
    const farData = new Float32Array(buffers.farStaging.getMappedRange().slice(0));

    buffers.nearStaging.unmap();
    buffers.farStaging.unmap();

    return { near: nearData, far: farData };
  }

  async readAllDepths(): Promise<Array<{ near: Float32Array; far: Float32Array }> | null> {
    if (!this.initialized || !this.device) return null;

    // Single encoder for all copy operations
    const encoder = this.device.createCommandEncoder();
    for (let i = 0; i < this.viewConfigs.length; i++) {
      const config = this.viewConfigs[i];
      const buffers = this.viewBuffers[i];
      const bufferSize = config.resolutionU * config.resolutionV * 4;
      encoder.copyBufferToBuffer(buffers.nearDepth, 0, buffers.nearStaging, 0, bufferSize);
      encoder.copyBufferToBuffer(buffers.farDepth, 0, buffers.farStaging, 0, bufferSize);
    }
    this.device.queue.submit([encoder.finish()]);

    // Map ALL staging buffers in parallel
    const mapPromises = this.viewBuffers.map(b =>
      Promise.all([
        b.nearStaging.mapAsync(GPUMapMode.READ),
        b.farStaging.mapAsync(GPUMapMode.READ)
      ])
    );
    await Promise.all(mapPromises);

    // Read all data
    const results = this.viewBuffers.map((buffers, i) => {
      const near = new Float32Array(buffers.nearStaging.getMappedRange().slice(0));
      const far = new Float32Array(buffers.farStaging.getMappedRange().slice(0));
      buffers.nearStaging.unmap();
      buffers.farStaging.unmap();
      return { near, far };
    });

    return results;
  }

  async flush(): Promise<void> {
    if (!this.device) return;
    if (typeof (this.device.queue as any).onSubmittedWorkDone === 'function') {
      await (this.device.queue as any).onSubmittedWorkDone();
    }
  }

  getDevice(): GPUDevice | null {
    return this.device;
  }

  getGridInfo(): {
    resXY: number;
    resXZ: number;
    resYZ: number;
    voxelSize: number;
    bbox: BBoxInfo | null;
  } {
    return {
      resXY: this.resXY,
      resXZ: this.resXZ,
      resYZ: this.resYZ,
      voxelSize: this.voxelSize,
      bbox: this.bbox
    };
  }

  isInitialized(): boolean {
    return this.initialized;
  }

  dispose(): void {
    if (!this.device) return;

    // Destroy all buffers
    this.uniformBuffer?.destroy();
    this.toolUniformBuffer?.destroy();
    this.dispatchUniformBuffer?.destroy();
    this.positionsBuffer?.destroy();

    for (const buffers of this.viewBuffers) {
      buffers.nearDepth.destroy();
      buffers.farDepth.destroy();
      buffers.nearStaging.destroy();
      buffers.farStaging.destroy();
    }

    this.viewBuffers = [];
    this.viewConfigs = [];
    this.viewBindGroups = [];
    this.solidInitDataPerView = [];
    this.device = null;
    this.initialized = false;
  }
}

/**
 * Factory function to create and initialize a ZBufferGPUEngine
 */
export async function createZBufferGPUEngine(
  bbox: BBoxInfo,
  voxelSize: number
): Promise<ZBufferGPUEngine | null> {
  const engine = new ZBufferGPUEngine();
  const success = await engine.initialize(bbox, voxelSize);

  if (!success) {
    engine.dispose();
    return null;
  }

  return engine;
}
